import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import pca, { authRequest } from '../../app/msal';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { env } from '../../../shared/env';

const rawBaseQuery = fetchBaseQuery({
  baseUrl:
    process.env.NODE_ENV === 'test'
      ? process.env.REACT_APP_API_BASE_URL
      : env.REACT_APP_API_BASE_URL,
  prepareHeaders: async (headers) => {
    const authSettings = {
      ...authRequest,
      account: pca.getAllAccounts()[0] as AccountInfo,
    };
    const getTokens = async (settings: any) => {
      return pca.acquireTokenSilent(settings).catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // fallback to interaction when silent call fails
          return pca.acquireTokenRedirect(settings);
        }
      });
    };
    // Request tokens from Azure AD (if needed)
    let response = await getTokens(authSettings);
    // If token will expire in less than 5 minutes, force a refresh of the token.
    if (
      !((response as any)?.idTokenClaims as any) ||
      ((response as any)?.idTokenClaims as any).exp <
        Math.floor(Date.now() / 1000) + 60 * 5
    ) {
      response = await getTokens({ ...authSettings, forceRefresh: true });
    }
    headers.set('Authorization', `Bearer ${(response as any)?.idToken}`);
    return headers;
  },
});

const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Provide a dynamic base query that attaches the correct base url.
  // This is necessary for easily integration testing with the backend because the REACT_APP_API_BASE_URL is dynamic and changes at the start of every test.
  const urlEnd = typeof args === 'string' ? args : args.url;
  // construct a dynamically generated portion of the url
  const adjustedUrl = `${
    process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_API_BASE_URL
      : env.REACT_APP_API_BASE_URL
  }/${urlEnd}`;
  const adjustedArgs =
    typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  // provide the amended url and other params to the raw base query
  return rawBaseQuery(adjustedArgs, api, extraOptions);
};

let keepUnusedDataFor = 60;
/* istanbul ignore if */
if (process.env.NODE_ENV === 'test') {
  // Do not cache data for tests
  keepUnusedDataFor = 0;
}
// Define a service using a base URL and expected endpoints
export const sepAPIWithOutTags = createApi({
  reducerPath: 'sepAPI',
  baseQuery: dynamicBaseQuery,
  keepUnusedDataFor,
  endpoints: (builder) => ({
    // getUser: builder.query<User, string>({
    //   query: (id) => `users/${id}`,
    // }),
    // updateUser: builder.mutation<User, Partial<User> & Pick<User, 'id'>>({
    //     query: ({ id, ...patch }) => {
    //         return {
    //             url: `users/${id}`,
    //             method: 'PATCH',
    //             body: patch,
    //         }
    //     },
    // })
  }),
});

export const sepAPI = sepAPIWithOutTags.enhanceEndpoints({
  addTagTypes: [
    'SEPs',
    'Tasks',
    'TaskTemplate',
    'TasksForSEP',
    'Comments',
    'DefaultStakeholdersForSEP',
    'DataFields',
    'Attachments',
    'Activities',
    'Knockout-Templates',
    'Followups',
    'Departments',
    'Settings',
    'DepartmentLookups',
    'NotificationLogs',
    'EmailNotification',
    'EmailNotificationLocation',
    'UserNotes',
    'LogicAppSettings',
    'AISummary',
    'SepPromptSettings',
    'SepPromptSettingsHistory',
  ],
});
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
//export const { useGetUserQuery } = sepAPI;
